import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Title1,
  Subtitle2,
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Input,
  Dropdown,
  Option,
  Button,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  tokens,
} from '@fluentui/react-components'
import { ArrowSync20Regular, CloudRegular, Dismiss16Regular, Search20Regular, ServerRegular } from '@fluentui/react-icons'
import { apiFetchJson } from '../lib/apiFetch'
import { useMe } from '../lib/me'
import Lozenge from '../components/Lozenge'
import type { PersonDirectoryResponse, PersonDirectoryRow } from '../types'

/**
 * Fixed per-column pixel widths, applied identically to header and body cells. Entra fields stay
 * as separate columns (office/city/country are 3 distinct Entra attributes, not one — a future
 * cross-service comparison needs them separately, not pre-joined into one display string). Widths
 * are generous enough that real values rarely need the ellipsis fallback; combined with
 * `whiteSpace: 'nowrap'` this is what makes the table wider than its container so the wrapping
 * scroll box actually scrolls, instead of every column being squeezed down to the point where
 * adjacent cells' text visually ran into each other (an earlier bug).
 */
const COLUMN_WIDTHS = {
  email: 240,
  displayName: 170,
  status: 100,
  type: 80,
  department: 150,
  jobTitle: 170,
  manager: 170,
  officeLocation: 150,
  city: 120,
  country: 120,
  phone: 140,
  directorySynced: 130,
  created: 110,
  lastSignIn: 110,
} as const

function cellStyle(width: number): React.CSSProperties {
  return { width, minWidth: width, maxWidth: width, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
}

const STATUS_OPTIONS = [
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'unknown', label: 'Unknown' },
]
const SYNCED_OPTIONS = [
  { value: 'yes', label: 'Yes (hybrid, on-premises AD)' },
  { value: 'no', label: 'No (cloud-native)' },
  { value: 'unknown', label: 'Unknown' },
]

function syncedValue(row: PersonDirectoryRow): 'yes' | 'no' | 'unknown' {
  if (row.onPremisesSyncEnabled === null) return 'unknown'
  return row.onPremisesSyncEnabled ? 'yes' : 'no'
}

function matchesFilters(
  row: PersonDirectoryRow,
  search: string,
  statusFilter: string[],
  typeFilter: string[],
  departmentFilter: string[],
  syncedFilter: string[],
): boolean {
  if (search) {
    const q = search.toLowerCase()
    const hit = [row.email, row.displayName, row.department, row.jobTitle].some(v => v?.toLowerCase().includes(q))
    if (!hit) return false
  }
  if (statusFilter.length > 0 && !statusFilter.includes(row.entraStatus)) return false
  if (typeFilter.length > 0 && !typeFilter.includes(row.userType ?? '')) return false
  if (departmentFilter.length > 0 && !departmentFilter.includes(row.department ?? '')) return false
  if (syncedFilter.length > 0 && !syncedFilter.includes(syncedValue(row))) return false
  return true
}

/** label/value pairs shown in the detail dialog — same fields as the table columns, just laid
 * out for reading in full rather than a fixed-width cell. */
function detailFields(row: PersonDirectoryRow): { label: string; value: string }[] {
  return [
    { label: 'Email', value: row.email },
    { label: 'Status', value: row.entraStatus },
    { label: 'Account type', value: row.userType ?? '—' },
    { label: 'Department', value: row.department ?? '—' },
    { label: 'Job title', value: row.jobTitle ?? '—' },
    { label: 'Manager', value: row.managerDisplayName ?? '—' },
    { label: 'Office location', value: row.officeLocation ?? '—' },
    { label: 'City', value: row.city ?? '—' },
    { label: 'Country', value: row.country ?? '—' },
    { label: 'Usage location', value: row.usageLocation ?? '—' },
    { label: 'Mobile phone', value: row.mobilePhone ?? '—' },
    { label: 'Business phone', value: row.businessPhone ?? '—' },
    {
      label: 'Directory synced',
      value: row.onPremisesSyncEnabled === null ? '—' : row.onPremisesSyncEnabled ? 'Synced from on-premises' : 'In cloud',
    },
    { label: 'Created', value: row.createdAt ?? '—' },
    { label: 'Last sign-in', value: row.lastSignInAt ?? '—' },
  ]
}

function UserDetailDialog(props: { row: PersonDirectoryRow | null; onClose: () => void }) {
  const { row, onClose } = props
  return (
    <Dialog open={!!row} onOpenChange={(_e, data) => !data.open && onClose()}>
      <DialogSurface>
        {row && (
          <DialogBody>
            <DialogTitle>{row.displayName}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 8, columnGap: 16 }}>
                {detailFields(row).map(f => (
                  <Fragment key={f.label}>
                    <strong>{f.label}</strong>
                    <span>{f.value}</span>
                  </Fragment>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={onClose}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        )}
      </DialogSurface>
    </Dialog>
  )
}

export default function Users() {
  const [data, setData] = useState<PersonDirectoryResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([])
  const [syncedFilter, setSyncedFilter] = useState<string[]>([])
  const [selectedRow, setSelectedRow] = useState<PersonDirectoryRow | null>(null)
  const me = useMe()

  function load() {
    setLoading(true)
    apiFetchJson<PersonDirectoryResponse>('/api/persons')
      .then(setData)
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const typeOptions = useMemo(
    () => [...new Set((data?.persons ?? []).map(p => p.userType).filter((v): v is string => !!v))].sort(),
    [data],
  )
  const departmentOptions = useMemo(
    () => [...new Set((data?.persons ?? []).map(p => p.department).filter((v): v is string => !!v))].sort(),
    [data],
  )

  const rows = useMemo(
    () =>
      (data?.persons ?? [])
        .filter(p => matchesFilters(p, search, statusFilter, typeFilter, departmentFilter, syncedFilter))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [data, search, statusFilter, typeFilter, departmentFilter, syncedFilter],
  )

  if (!me.isAdmin) {
    return (
      <div>
        <Title1>Users</Title1>
        <p style={{ marginTop: 16 }}>Admin access required.</p>
      </div>
    )
  }
  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading users..." style={{ marginTop: 24 }} />

  return (
    // height: '100%' rides main's flex-stretched (definite) height in App.tsx, and flexDirection
    // column + the scroll wrapper's flex: 1 below is what makes the table fill exactly the
    // remaining space down to the bottom of the window, instead of a guessed calc(100vh - Npx)
    // leaving a dead gap underneath (a shorter guess) or clipping (a taller one).
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title1>Users</Title1>
        <Button appearance="secondary" icon={<ArrowSync20Regular />} onClick={load} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
      <Subtitle2 style={{ display: 'block', marginTop: 8, marginBottom: 4 }}>
        Everyone synced from Entra ID ({data.persons.length} total)
      </Subtitle2>
      <p style={{ marginTop: 0, marginBottom: 16, color: tokens.colorNeutralForeground3 }}>
        This is exactly what the directory sync captured — for confirming it's working and seeing
        what data is available. A full directory sync (not just a refetch) is triggered from the
        account menu → Settings → "Sync directory now". Click a row for full details.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Input
          contentBefore={<Search20Regular />}
          contentAfter={
            search && (
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss16Regular />}
                onClick={() => setSearch('')}
                aria-label="Clear search"
              />
            )
          }
          placeholder="Search by name, email, department, or job title..."
          value={search}
          onChange={(_e, d) => setSearch(d.value)}
          style={{ minWidth: 260 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Dropdown
            placeholder="Status"
            multiselect
            selectedOptions={statusFilter}
            value={statusFilter.map(v => STATUS_OPTIONS.find(o => o.value === v)?.label ?? v).join(', ')}
            onOptionSelect={(_e, d) => setStatusFilter(d.selectedOptions)}
            style={{ minWidth: 140 }}
          >
            {STATUS_OPTIONS.map(o => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Dropdown>
          {statusFilter.length > 0 && (
            <Button appearance="transparent" size="small" icon={<Dismiss16Regular />} onClick={() => setStatusFilter([])} aria-label="Clear status filter" />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Dropdown
            placeholder="Type"
            multiselect
            selectedOptions={typeFilter}
            value={typeFilter.join(', ')}
            onOptionSelect={(_e, d) => setTypeFilter(d.selectedOptions)}
            style={{ minWidth: 130 }}
          >
            {typeOptions.map(o => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Dropdown>
          {typeFilter.length > 0 && (
            <Button appearance="transparent" size="small" icon={<Dismiss16Regular />} onClick={() => setTypeFilter([])} aria-label="Clear type filter" />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Dropdown
            placeholder="Department"
            multiselect
            selectedOptions={departmentFilter}
            value={departmentFilter.length === 1 ? departmentFilter[0] : departmentFilter.length > 1 ? `${departmentFilter.length} selected` : ''}
            onOptionSelect={(_e, d) => setDepartmentFilter(d.selectedOptions)}
            style={{ minWidth: 160 }}
          >
            {departmentOptions.map(o => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Dropdown>
          {departmentFilter.length > 0 && (
            <Button appearance="transparent" size="small" icon={<Dismiss16Regular />} onClick={() => setDepartmentFilter([])} aria-label="Clear department filter" />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Dropdown
            placeholder="Directory synced"
            multiselect
            selectedOptions={syncedFilter}
            value={syncedFilter.map(v => SYNCED_OPTIONS.find(o => o.value === v)?.label ?? v).join(', ')}
            onOptionSelect={(_e, d) => setSyncedFilter(d.selectedOptions)}
            style={{ minWidth: 160 }}
            listbox={{ style: { minWidth: 260 } }}
          >
            {SYNCED_OPTIONS.map(o => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Dropdown>
          {syncedFilter.length > 0 && (
            <Button appearance="transparent" size="small" icon={<Dismiss16Regular />} onClick={() => setSyncedFilter([])} aria-label="Clear synced filter" />
          )}
        </div>
      </div>

      {/* flex: 1 + minHeight: 0 (the classic flex-child gotcha — without minHeight: 0 a flex item
          won't shrink below its content's natural height, so overflow would never trigger) makes
          this fill exactly the remaining vertical space, with its own scroll on both axes —
          otherwise the horizontal scrollbar only appears after scrolling past every row (hundreds
          of them) rather than staying reachable, or the table sits in an undersized fixed-height
          box leaving dead space below it. */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}` }}>
        <Table style={{ tableLayout: 'fixed' }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.email)}>Email</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.displayName)}>Display name</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.status)}>Status</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.type)}>Type</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.department)}>Department</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.jobTitle)}>Job title</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.manager)}>Manager</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.officeLocation)}>Office location</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.city)}>City</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.country)}>Country</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.phone)}>Phone</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.directorySynced)}>
                <Tooltip
                  content="Same as Entra's own 'Sync status' column: a server icon = a hybrid account synced in from an on-premises Active Directory (via Entra Connect/Cloud Sync); a cloud icon = a cloud-native account created directly in Entra ID."
                  relationship="description"
                  withArrow
                >
                  <span>Directory synced</span>
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.created)}>Created</TableHeaderCell>
              <TableHeaderCell style={cellStyle(COLUMN_WIDTHS.lastSignIn)}>
                <Tooltip
                  content="Requires the AuditLog.Read.All Graph permission — see the sync result message on Settings → General if this is empty for everyone."
                  relationship="description"
                  withArrow
                >
                  <span>Last sign-in</span>
                </Tooltip>
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.email} onClick={() => setSelectedRow(row)} style={{ cursor: 'pointer' }}>
                <TableCell style={cellStyle(COLUMN_WIDTHS.email)}>{row.email}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.displayName)}>{row.displayName}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.status)}>
                  <Lozenge color={row.entraStatus === 'enabled' ? 'success' : 'danger'}>
                    {row.entraStatus === 'enabled' ? 'Enabled' : row.entraStatus === 'disabled' ? 'Disabled' : 'Unknown'}
                  </Lozenge>
                </TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.type)}>{row.userType ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.department)}>{row.department ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.jobTitle)}>{row.jobTitle ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.manager)}>{row.managerDisplayName ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.officeLocation)}>{row.officeLocation ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.city)}>{row.city ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.country)}>{row.country ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.phone)}>{row.mobilePhone ?? row.businessPhone ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.directorySynced)}>
                  {row.onPremisesSyncEnabled === null ? (
                    '—'
                  ) : row.onPremisesSyncEnabled ? (
                    <Tooltip content="Synced from on-premises" relationship="label" withArrow>
                      <ServerRegular />
                    </Tooltip>
                  ) : (
                    <Tooltip content="In cloud" relationship="label" withArrow>
                      <CloudRegular />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.created)}>{row.createdAt ?? '—'}</TableCell>
                <TableCell style={cellStyle(COLUMN_WIDTHS.lastSignIn)}>{row.lastSignInAt ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.length === 0 && <p>No users match the current filters.</p>}

      <UserDetailDialog row={selectedRow} onClose={() => setSelectedRow(null)} />
    </div>
  )
}
